import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api-client';
import type { ApiValidationIssue } from '@/lib/api-client';
import { WizardContainer } from '@/components/wizard/wizard-container';
import { WizardProgress } from '@/components/wizard/wizard-progress';
import { WizardNavigation } from '@/components/wizard/wizard-navigation';
import { StepRouting } from '@/components/contracts/b2b/step-routing';
import { StepConfig } from '@/components/contracts/b2b/step-config';
import { StepParties } from '@/components/contracts/b2b/step-parties';
import { StepSubject } from '@/components/contracts/b2b/step-subject';
import { StepTerms } from '@/components/contracts/b2b/step-terms';
import { B2bSummary } from '@/components/contracts/b2b/b2b-summary';
import { AdvancedAccordion } from '@/components/contracts/b2b/advanced-accordion';
import { useSession } from '@/lib/auth-client';
import { useContract, useContractTransition, useCreateContract } from '@/hooks/api/use-contracts';
import { useComments } from '@/hooks/api/use-comments';
import { useAutoSave } from '@/hooks/use-auto-save';
import { useWizard } from '@/hooks/use-wizard';
import { useB2bSmartDefaults } from '@/hooks/use-b2b-defaults';
import { SummaryReturnBanner } from '@/components/wizard/summary-return-banner';
import { NameContractModal } from '@/components/contracts/name-contract-modal';
import { StepCommentBanner } from '@/components/review/step-comment-banner';
import { getCommentCountsPerStep } from '@/components/review/comment-step-utils';
import type { Warning, Suggestion } from '@/components/wizard/wizard-summary';
import type { B2bWizardForm } from '@/components/contracts/b2b/types';

// --- Route definition ---

export const Route = createFileRoute('/_authenticated/contracts/new-b2b')({
  component: B2bWizardPage,
  validateSearch: (search: Record<string, unknown>): { id?: string } => ({
    id: (search.id as string) || undefined,
  }),
});

// --- Constants ---

const CORE_STEP_COUNT = 6; // Steps 1-5 + Summary (progress bar shows all 6)
const CORE_STEPS = 6; // Steps 1-5 + Summary (internal navigation)

// --- Page Component ---

function B2bWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { id: initialId } = Route.useSearch();
  const [contractId, setContractId] = useState(initialId ?? '');
  const contractIdRef = useRef(initialId ?? '');
  const createMutation = useCreateContract();

  // Mode: 'core' (steps 1-5 + summary) or 'advanced' (accordion)
  const [mode, setMode] = useState<'core' | 'advanced'>('core');
  const [submitErrors, setSubmitErrors] = useState<ApiValidationIssue[]>([]);
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  const defaultsAppliedRef = useRef(false);
  const [editingFromSummary, setEditingFromSummary] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [initialCategory, setInitialCategory] = useState<string | null>(null);
  const [routingSubScreen, setRoutingSubScreen] = useState<'a' | 'b'>('a');
  const [recommendedConfig, setRecommendedConfig] = useState<{
    compensation_model?: string;
    ip_model?: string;
    duration?: string;
  }>({});
  const [recommendedReasons, setRecommendedReasons] = useState<{
    compensation_model?: string;
    ip_model?: string;
    duration?: string;
  }>({});

  // React Hook Form
  const form = useForm<B2bWizardForm>({
    defaultValues: {
      routing: {},
      config: {},
      parties: { client: {}, contractor: {} },
      subject: {},
      terms: {},
      advanced: {},
      _meta: { form_version: '1.0', smart_defaults_applied: false, defaults_overridden: [] },
    },
  });

  // Load existing contract data
  const { data: contract, isLoading: isLoadingContract } = useContract(contractId, {
    enabled: !!contractId,
  });

  // Comments for CHANGES_REQUESTED state — comment badges + banners
  const isChangesRequested = contract?.status === 'CHANGES_REQUESTED';
  const { data: commentsData } = useComments(
    contractId,
    {},
    {
      enabled: !!contractId && isChangesRequested,
    },
  );
  const openComments = useMemo(
    () => (commentsData?.comments ?? []).filter((c) => c.status === 'OPEN'),
    [commentsData?.comments],
  );
  const commentCountsPerStep = useMemo(
    () =>
      isChangesRequested && openComments.length > 0
        ? getCommentCountsPerStep(openComments, 'B2B', CORE_STEP_COUNT)
        : [],
    [isChangesRequested, openComments],
  );

  // Populate form from existing contract — only on first load, not after auto-save refetches
  const hasRestoredForm = useRef(false);
  const hasRestoredStep = useRef(false);
  useEffect(() => {
    if (hasRestoredForm.current || !contract?.formData) return;
    const formData = contract.formData as unknown as B2bWizardForm;
    if (formData.routing) {
      form.reset(formData);
      hasRestoredForm.current = true;
      // If defaults were already applied in a previous session, skip re-applying them.
      // Use ref (synchronous) so the smart defaults effect in the same cycle sees it.
      if (formData._meta?.smart_defaults_applied) {
        defaultsAppliedRef.current = true;
        setDefaultsApplied(true);
      }
    }
  }, [contract, form]);

  // Smart defaults
  const cooperationModel = form.watch('routing.cooperation_model');
  const context = form.watch('routing.context');
  const perspective = form.watch('routing.perspective');

  const { data: defaultsResponse } = useB2bSmartDefaults({
    cooperationModel,
    context,
    perspective,
  });

  // Track previous routing to detect changes
  const prevRoutingRef = useRef({ cooperationModel, context, perspective });

  // Apply smart defaults — reset on routing change, apply when defaults arrive
  useEffect(() => {
    const prev = prevRoutingRef.current;
    const routingChanged =
      prev.cooperationModel != null &&
      (prev.cooperationModel !== cooperationModel ||
        prev.context !== context ||
        prev.perspective !== perspective);
    prevRoutingRef.current = { cooperationModel, context, perspective };

    if (routingChanged) {
      defaultsAppliedRef.current = false;
      setDefaultsApplied(false);
      setRecommendedConfig({});
      setRecommendedReasons({});
    }

    if (
      defaultsResponse &&
      !defaultsAppliedRef.current &&
      cooperationModel &&
      context &&
      perspective
    ) {
      const defaults = defaultsResponse.data as Record<string, unknown>;

      if (defaults?.config) {
        const configDefaults = defaults.config as Record<string, unknown>;
        setRecommendedConfig({
          compensation_model: configDefaults.compensation_model as string | undefined,
          ip_model: configDefaults.ip_model as string | undefined,
          duration: configDefaults.duration as string | undefined,
        });
        if (defaults.configReasons) {
          const reasons = defaults.configReasons as Record<string, string>;
          setRecommendedReasons({
            compensation_model: reasons.compensation_model,
            ip_model: reasons.ip_model,
            duration: reasons.duration,
          });
        }
        form.setValue('config', defaults.config as unknown as B2bWizardForm['config'], {
          shouldDirty: false,
        });
      }
      if (defaults?.advanced) {
        form.setValue('advanced', defaults.advanced as unknown as B2bWizardForm['advanced'], {
          shouldDirty: false,
        });
      }
      if (defaults?.terms) {
        const termsDefaults = defaults.terms as Record<string, unknown>;
        if (termsDefaults.payment_term_days != null) {
          form.setValue('terms.payment_term_days', termsDefaults.payment_term_days as number, {
            shouldDirty: false,
          });
        }
        if (termsDefaults.invoicing_frequency != null) {
          form.setValue('terms.invoicing_frequency', termsDefaults.invoicing_frequency as string, {
            shouldDirty: false,
          });
        }
        if (termsDefaults.advance_type != null) {
          form.setValue(
            'terms.advance_type',
            termsDefaults.advance_type as B2bWizardForm['terms']['advance_type'],
            { shouldDirty: false },
          );
        }
        if (termsDefaults.set_off != null) {
          form.setValue(
            'terms.set_off',
            termsDefaults.set_off as B2bWizardForm['terms']['set_off'],
            { shouldDirty: false },
          );
        }
        if (termsDefaults.valorization != null) {
          form.setValue('terms.valorization', termsDefaults.valorization as boolean, {
            shouldDirty: false,
          });
        }
      }
      if (defaults?.subject) {
        const subjectDefaults = defaults.subject as Record<string, unknown>;
        if (subjectDefaults.scope_type != null) {
          form.setValue(
            'subject.scope_type',
            subjectDefaults.scope_type as B2bWizardForm['subject']['scope_type'],
            { shouldDirty: false },
          );
        }
        if (subjectDefaults.change_request_enabled != null) {
          form.setValue(
            'subject.change_request_enabled',
            subjectDefaults.change_request_enabled as boolean,
            { shouldDirty: false },
          );
        }
        if (subjectDefaults.location != null) {
          form.setValue(
            'subject.location',
            subjectDefaults.location as B2bWizardForm['subject']['location'],
            { shouldDirty: false },
          );
        }
      }
      form.setValue('_meta.smart_defaults_applied', true, { shouldDirty: false });
      defaultsAppliedRef.current = true;
      setDefaultsApplied(true);
    }
  }, [defaultsResponse, defaultsApplied, cooperationModel, context, perspective, form]);

  // Draft edit mode — transactional: no auto-save, only save+regenerate on submit
  const isDraftEdit = contract?.status === 'DRAFT' || contract?.status === 'DRAFT_SELECTION';

  // Auto-save
  const { save, saveImmediate } = useAutoSave({ contractId, enabled: !!contractId });

  // Core wizard
  const wizard = useWizard({
    totalSteps: CORE_STEPS,
    initialStep: 0,
    onStepChange: (_from, _to) => {
      window.scrollTo(0, 0);
      if (_to === CORE_STEPS - 1) setEditingFromSummary(false);
      if (contractId) {
        form.setValue('_meta.current_step', _to, { shouldDirty: false });
        if (!isDraftEdit) {
          save(form.getValues() as unknown as Record<string, unknown>, _to);
        }
      }
    },
    validateStep: async (step) => {
      if (step === 0) {
        // Step 1: require all routing fields
        if (!(cooperationModel && context && perspective)) return false;
        // Create contract on first advance past step 1
        if (!contractIdRef.current) {
          try {
            const response = await createMutation.mutateAsync({ contractType: 'B2B' });
            const newId = (response as { data: { id: string } }).data.id;
            contractIdRef.current = newId;
            setContractId(newId);
            void navigate({
              to: '/contracts/new-b2b',
              search: { id: newId },
              replace: true,
            });
          } catch {
            toast.error(t('contractsList.createError'));
            return false;
          }
        }
        return true;
      }
      if (step === 1) {
        // Step 2: require all config fields
        const config = form.getValues('config');
        return !!(config?.compensation_model && config?.ip_model && config?.duration);
      }
      if (step === 2) {
        // Step 3: require legal form selected for both parties
        const clientForm = form.getValues('parties.client.legal_form');
        const contractorForm = form.getValues('parties.contractor.legal_form');
        return !!(clientForm && contractorForm);
      }
      if (step === 3) {
        // Step 4: require scope type and description (min 10 chars)
        const scopeType = form.getValues('subject.scope_type');
        const description = form.getValues('subject.description') ?? '';
        return !!(scopeType && description.trim().length >= 10);
      }
      if (step === 4) {
        // Step 5: require amount > 0 and payment term days
        const amount = form.getValues('terms.amount');
        const paymentDays = form.getValues('terms.payment_term_days');
        return !!(amount && amount > 0 && paymentDays && paymentDays >= 1 && paymentDays <= 120);
      }
      return true;
    },
  });

  // Resume wizard at the saved step (after contract + wizard are ready)
  useEffect(() => {
    if (hasRestoredStep.current || !contract?.formData) return;
    const meta = (contract.formData as Record<string, unknown>)?._meta as
      | Record<string, unknown>
      | undefined;
    const savedStep = meta?.current_step as number | undefined;
    if (typeof savedStep === 'number' && savedStep > 0 && savedStep < CORE_STEPS) {
      wizard.jumpToStep(savedStep);
    }
    hasRestoredStep.current = true;
  }, [contract, wizard]);

  // Transition mutation (submit)
  const transition = useContractTransition(contractId);

  // Warnings & suggestions from defaults + form-reactive
  const warnings: Warning[] = [];
  const suggestions: Suggestion[] = [];

  if (defaultsResponse) {
    const data = defaultsResponse.data as Record<string, unknown>;
    if (Array.isArray(data?.warnings)) {
      for (const w of data.warnings as Array<{ id: string; message: string; tier: number }>) {
        warnings.push({ message: w.message, tier: w.tier as 1 | 2 | 3 });
      }
    }
    if (Array.isArray(data?.suggestions)) {
      for (const s of data.suggestions as Array<{ id: string; message: string }>) {
        suggestions.push({ message: s.message });
      }
    }
  }

  // --- Form-reactive warnings (B2B-specific) ---

  // Parties warnings
  const clientLegalForm = form.watch('parties.client.legal_form');
  const contractorLegalForm = form.watch('parties.contractor.legal_form');

  if (clientLegalForm === 'spolka_cywilna' || contractorLegalForm === 'spolka_cywilna') {
    warnings.push({ message: t('wizard.warnings.spolkaCywilna'), tier: 1 });
  }
  if (clientLegalForm === 'foreign_entity' || contractorLegalForm === 'foreign_entity') {
    warnings.push({ message: t('wizard.warnings.foreignEntity'), tier: 2 });
  }

  // Subject warnings
  const exclusivityVal = form.watch('subject.exclusivity');
  if (exclusivityVal) {
    warnings.push({ message: t('wizard.warnings.exclusivitySelected'), tier: 3 });
  }

  // Terms warnings
  const paymentTermDays = form.watch('terms.payment_term_days');
  if (paymentTermDays && paymentTermDays > 60) {
    warnings.push({ message: t('wizard.warnings.longPaymentTerm'), tier: 2 });
  }

  const termsAmount = form.watch('terms.amount');
  const termsAdvanceAmount = form.watch('terms.advance_amount');
  const termsAdvanceType = form.watch('terms.advance_type');
  if (termsAmount && termsAdvanceAmount && termsAdvanceAmount > termsAmount) {
    warnings.push({ message: t('wizard.warnings.advanceExceedsAmount'), tier: 1 });
  }
  if (termsAdvanceType === 'deposit') {
    warnings.push({ message: t('wizard.warnings.depositSelected'), tier: 3 });
  }

  const termsSetOff = form.watch('terms.set_off');
  if (termsSetOff === 'broad') {
    warnings.push({ message: t('wizard.warnings.broadSetOff'), tier: 2 });
  }

  // Advanced warnings
  const penaltiesEnabled = form.watch('advanced.liability.penalties_enabled');
  const capEnabled = form.watch('advanced.liability.cap_enabled');
  const indemnification = form.watch('advanced.liability.indemnification');
  const personalData = form.watch('advanced.ip_confidentiality.personal_data');
  const governingLaw = form.watch('advanced.final_provisions.governing_law');
  const executionForm = form.watch('advanced.final_provisions.execution_form');

  if (cooperationModel === 'body_leasing' && !indemnification) {
    warnings.push({ message: t('wizard.warnings.bodyLeasingNoIndemnification'), tier: 1 });
  }
  if (penaltiesEnabled === false) {
    warnings.push({ message: t('wizard.warnings.noPenalties'), tier: 2 });
  }
  if (capEnabled === false) {
    warnings.push({ message: t('wizard.warnings.noCapOnLiability'), tier: 3 });
  }
  if (personalData) {
    warnings.push({ message: t('wizard.warnings.personalDataB2b'), tier: 2 });
  }
  if (governingLaw && governingLaw !== 'polish') {
    warnings.push({ message: t('wizard.warnings.foreignLaw'), tier: 2 });
  }
  if (executionForm === 'electronic' || executionForm === 'scans') {
    warnings.push({ message: t('wizard.warnings.electronicForm'), tier: 2 });
  }

  // Submit handler — for new contracts, opens name modal first
  const handleSubmit = useCallback(() => {
    if (!contractId) return;
    if (!isDraftEdit) {
      setNameModalOpen(true);
      return;
    }
    executeGeneration();
  }, [contractId, isDraftEdit]);

  const executeGeneration = useCallback(async () => {
    if (!contractId) return;
    setSubmitErrors([]);

    try {
      await saveImmediate(
        form.getValues() as unknown as Record<string, unknown>,
        wizard.currentStep,
      );

      const action = isDraftEdit ? 'regenerate' : 'submit';
      transition.mutate(
        { action },
        {
          onSuccess: (res) => {
            const identifier = (res as { data?: { contractIdentifier?: string } })?.data
              ?.contractIdentifier;
            toast.success(
              identifier
                ? t('wizard.generateSuccess', { identifier })
                : t('wizard.generateSuccessGeneric'),
            );
            navigate({ to: '/contracts', search: { status: undefined } });
          },
          onError: (error) => {
            if (error instanceof ApiError && error.issues?.length) {
              setSubmitErrors(error.issues);
            } else {
              toast.error(t('contractsList.createError'));
            }
          },
        },
      );
    } catch {
      toast.error(t('contractsList.createError'));
    }
  }, [contractId, form, wizard.currentStep, saveImmediate, transition, navigate, t, isDraftEdit]);

  const handleNameConfirm = useCallback(
    async (title: string) => {
      if (!contractId) return;
      try {
        await api.patch(`/contracts/${contractId}/title`, { title });
      } catch {
        // Non-blocking
      }
      setNameModalOpen(false);
      executeGeneration();
    },
    [contractId, executeGeneration],
  );

  // Enter advanced mode (always from summary), optionally open a specific category
  const handleCustomize = useCallback((openCategory?: string) => {
    setEditingFromSummary(true);
    setMode('advanced');
    setInitialCategory(openCategory ?? null);
    window.scrollTo(0, 0);
  }, []);

  // Return from advanced mode
  const handleAdvancedBack = useCallback(() => {
    setMode('core');
    if (contractId && !isDraftEdit) {
      save(form.getValues() as unknown as Record<string, unknown>, CORE_STEPS - 1);
    }
  }, [contractId, form, save, isDraftEdit]);

  // Step labels for progress bar — show all 6 steps including summary
  const coreStepLabels = [
    t('b2bWizard.stepLabels.model'),
    t('b2bWizard.stepLabels.config'),
    t('b2bWizard.stepLabels.parties'),
    t('b2bWizard.stepLabels.subject'),
    t('b2bWizard.stepLabels.terms'),
    t('wizard.summary'),
  ];

  const progressSteps = coreStepLabels.map((label, i) => ({
    label,
    hasError: wizard.stepErrors[i],
    hasWarning: wizard.stepWarnings[i],
    commentCount: commentCountsPerStep[i],
  }));

  // Loading state
  if (contractId && isLoadingContract) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Guard: only creator can edit the form
  if (contract && session?.user?.id && contract.createdBy !== session.user.id) {
    void navigate({ to: '/contracts/$contractId', params: { contractId } });
    return null;
  }

  // Advanced mode rendering — accordion
  if (mode === 'advanced') {
    return (
      <WizardContainer title={t('b2bWizard.title')} subtitle={t('b2bWizard.subtitle')}>
        <div className="flex flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 lg:px-8">
            <SummaryReturnBanner
              onReturn={() => {
                setEditingFromSummary(false);
                setMode('core');
                wizard.jumpToStep(CORE_STEPS - 1);
                if (contractId && !isDraftEdit) {
                  save(form.getValues() as unknown as Record<string, unknown>, CORE_STEPS - 1);
                }
                window.scrollTo(0, 0);
              }}
            />
            <AdvancedAccordion
              form={form}
              defaultsApplied={defaultsApplied}
              onBack={handleAdvancedBack}
              initialOpen={initialCategory}
            />
          </div>
        </div>
      </WizardContainer>
    );
  }

  // Core mode rendering
  const isSummaryStep = wizard.currentStep === CORE_STEPS - 1;

  return (
    <>
      <WizardContainer title={t('b2bWizard.title')} subtitle={t('b2bWizard.subtitle')}>
        <div className="flex flex-1 flex-col">
          {/* Progress bar — show all 6 steps including summary */}
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            <WizardProgress
              steps={progressSteps}
              currentStep={wizard.currentStep}
              totalSteps={CORE_STEP_COUNT}
            />
          </div>

          {/* Step content */}
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6 lg:px-8">
            {editingFromSummary && !isSummaryStep && (
              <SummaryReturnBanner
                onReturn={() => {
                  setEditingFromSummary(false);
                  wizard.jumpToStep(CORE_STEPS - 1);
                  if (contractId && !isDraftEdit) {
                    save(form.getValues() as unknown as Record<string, unknown>, CORE_STEPS - 1);
                  }
                  window.scrollTo(0, 0);
                }}
              />
            )}
            {isChangesRequested && !isSummaryStep && openComments.length > 0 && (
              <StepCommentBanner
                comments={openComments}
                contractType="B2B"
                step={wizard.currentStep}
              />
            )}
            {wizard.currentStep === 0 && (
              <StepRouting
                form={form}
                direction={wizard.direction}
                subScreen={routingSubScreen}
                onSubScreenChange={setRoutingSubScreen}
              />
            )}
            {wizard.currentStep === 1 && (
              <StepConfig
                form={form}
                direction={wizard.direction}
                defaultsApplied={defaultsApplied}
                recommendedConfig={recommendedConfig}
                recommendedReasons={recommendedReasons}
              />
            )}
            {wizard.currentStep === 2 && <StepParties form={form} direction={wizard.direction} />}
            {wizard.currentStep === 3 && (
              <StepSubject
                form={form}
                direction={wizard.direction}
                defaultsApplied={defaultsApplied}
              />
            )}
            {wizard.currentStep === 4 && (
              <StepTerms
                form={form}
                direction={wizard.direction}
                defaultsApplied={defaultsApplied}
              />
            )}
            {isSummaryStep && (
              <B2bSummary
                form={form}
                warnings={warnings}
                suggestions={suggestions}
                onSubmit={handleSubmit}
                onCustomize={handleCustomize}
                isSubmitting={transition.isPending}
                onGoToStep={(step) => {
                  setEditingFromSummary(true);
                  wizard.jumpToStep(step);
                  window.scrollTo(0, 0);
                }}
                defaultsApplied={defaultsApplied}
                submitLabel={isDraftEdit ? t('wizard.regenerate') : undefined}
                submitErrors={submitErrors}
              />
            )}
          </div>

          {/* Navigation — show on all steps including summary (back button) */}
          {!isSummaryStep && !(wizard.currentStep === 0 && routingSubScreen === 'a') && (
            <WizardNavigation
              onBack={
                wizard.currentStep === 0 && routingSubScreen === 'b'
                  ? () => setRoutingSubScreen('a')
                  : wizard.goBack
              }
              onNext={wizard.goNext}
              isFirstStep={
                wizard.currentStep === 0 && routingSubScreen === 'b' ? false : wizard.isFirstStep
              }
              isLastStep={false}
              nextLabel={wizard.currentStep === 4 ? t('wizard.summary') : undefined}
              canGoNext={
                wizard.currentStep === 0
                  ? !!(cooperationModel && context && perspective)
                  : wizard.currentStep === 1
                    ? !!(
                        form.watch('config.compensation_model') &&
                        form.watch('config.ip_model') &&
                        form.watch('config.duration')
                      )
                    : wizard.currentStep === 2
                      ? !!(
                          form.watch('parties.client.legal_form') &&
                          form.watch('parties.contractor.legal_form')
                        )
                      : wizard.currentStep === 3
                        ? !!(
                            form.watch('subject.scope_type') &&
                            (form.watch('subject.description') ?? '').trim().length >= 10
                          )
                        : wizard.currentStep === 4
                          ? !!(
                              form.watch('terms.amount') &&
                              (form.watch('terms.amount') ?? 0) > 0 &&
                              form.watch('terms.payment_term_days') &&
                              (form.watch('terms.payment_term_days') ?? 0) >= 1 &&
                              (form.watch('terms.payment_term_days') ?? 0) <= 120
                            )
                          : true
              }
            />
          )}
          {isSummaryStep && (
            <WizardNavigation onBack={wizard.goBack} isFirstStep={false} isLastStep={false} />
          )}
        </div>
      </WizardContainer>

      <NameContractModal
        open={nameModalOpen}
        onOpenChange={setNameModalOpen}
        contractType="B2B"
        contractId={contractId ?? ''}
        onConfirm={handleNameConfirm}
        isPending={transition.isPending}
      />
    </>
  );
}
